import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../../core/redux/IAppState';
import { API_REDEEM_ENTITLEMENT } from '../../../core/redux/api/endpoints';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../core/redux/api/module';
import { Redemption } from './redemption';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../../core/redux/api/circuitBreaker/module';
import { GuestDetails } from '../../guestDetails';
import { assoc, isNilOrEmpty } from '../../../language/module';
import { toLocalISOString } from '../../../language/time';


const KEY_ACTION = ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST,
  ACTION_TYPE.SUBMIT_LOCAL_REF_RESPONSE,
  ACTION_TYPE.SUBMIT_LOCAL_REF_ERROR,
];


/**
 * Creates an action that can be dispatched to associate an entitlements redemption with a specific local reference. This
 * local reference is just for resorts to tie transactions in their local system to transactions in the IKON system.
 * @param redemption - A redemption object containing:
 * - guestId - The master guest ID for the guest whose entitlements we want redeem
 * - entitlementId - The ID of the entitlements we're looking to redeem
 * - redemptionDate - Just pass current time here -- you just need to hold on to this value so you can POST the
 * local reference associated with this redemption down the line.
 * - redemptionQuantity - The number of items to redeem from this entitlements
 * - localRef - the reference you want to send
 * @param guest - The guest this redemption was made against
 */
export const submitRedemptionLocalRefAction = (
  redemption: Redemption,
  guest: GuestDetails,
) => makeApiAction(
  {
    method:   'POST',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: API_REDEEM_ENTITLEMENT,
    // Entitlement Description intentionally blank
    body:     (state: IAppState) => JSON.stringify({
      "userId": state.userState.userId,
      "redemptionIdReference": redemption.redemptionId ?? null,
      "masterGuestId": redemption.guestId,
      "entitlementId": redemption.entitlementId,
      "entitlementDescription": "",
      "redemptionDate": toLocalISOString(redemption.redemptionDate),
      "redemptionQuantity": redemption.redemptionQuantity,
      "redemptionExternalCode": !isNilOrEmpty(redemption.localRef) ? redemption.localRef : null, 
      "redemptionLocation": "ResortTools",
      "resort": state.selectedResort.resortAbbreviation,
      "modifiedById": state.userState.email
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { redemption, guest } })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, redemption.guestId, KEY_ACTION)
                                    || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<any> = (state, action, error, isLoading, payload) => {
  const metaGuest = action.meta.guest;
  const guestId   = metaGuest.masterGuestId;

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error, { guest: metaGuest });
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  return assoc('redeemedEntitlement', undefined, noLoadingOrError);
};


export const submitRedemptionLocalRefReducer = makeApiReducer<any>(ACTIONS, KEY_ACTION, reducer);
